import React, { useEffect, useState, useRef, Fragment } from 'react';
import { MessageSquareIcon, XIcon, SendIcon, BotIcon, UserIcon, AlertTriangleIcon, PhoneIcon } from 'lucide-react';
interface ChatMessage {
  id: number;
  type: 'bot' | 'user';
  text: string;
  timestamp: string;
}
export function ChatWidget() {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<ChatMessage[]>([{
    id: 1,
    type: 'bot',
    text: "Welcome to Super International Travels! I'm your AI travel assistant. How can I help you today?",
    timestamp: new Date().toLocaleTimeString([], {
      hour: '2-digit',
      minute: '2-digit'
    })
  }]);
  const [input, setInput] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({
      behavior: 'smooth'
    });
  }, [messages, isTyping]);
  const quickActions = ['Visa Requirements', 'Travel Policy', 'Booking Help', 'Approval Status', 'FAQs'];
  const getBotResponse = (userMessage: string): string => {
    const lower = userMessage.toLowerCase();
    if (lower.includes('visa')) {
      return 'For visa requirements, I can provide general guidance based on your destination. Most business travelers to the EU, UK, and Japan can enter visa-free for short stays with a US passport. For specific requirements, please check with the relevant embassy. Note: This is general guidance only, not legal or immigration advice.';
    }
    if (lower.includes('policy') || lower.includes('travel policy')) {
      return "Your organization's travel policy includes: Domestic flight cap of $500/ticket, International flight cap of $1,500/ticket, Hotel nightly cap of $300. All trips require manager approval. Trips over $2,500 require department head approval. Would you like more details?";
    }
    if (lower.includes('booking') || lower.includes('book')) {
      return "To make a booking: 1) Go to 'Book Travel' from the sidebar, 2) Search for flights, hotels, or car rentals, 3) Select your preferred option, 4) Submit for approval. Your booking will go through the approval workflow before ticketing.";
    }
    if (lower.includes('approval') || lower.includes('status')) {
      return 'You currently have 2 pending approvals. REQ-2024-001 is awaiting Department Head approval (18h elapsed). REQ-2024-002 is awaiting Manager approval (5h elapsed). Would you like me to show more details?';
    }
    if (lower.includes('faq') || lower.includes('help')) {
      return 'Here are common questions I can help with:\n• How to submit a travel request\n• Understanding refund policies\n• Budget limits and overrides\n• Expense report submission\n• Cancellation procedures\n\nWhat would you like to know more about?';
    }
    return 'I can help you with visa requirements, travel policies, booking steps, approval status, and FAQs. What would you like to know? If you need specialized assistance, I can escalate to our human support team.';
  };
  const handleSend = (e: React.FormEvent) => {
    e.preventDefault();
    if (!input.trim()) return;
    const userMsg: ChatMessage = {
      id: Date.now(),
      type: 'user',
      text: input,
      timestamp: new Date().toLocaleTimeString([], {
        hour: '2-digit',
        minute: '2-digit'
      })
    };
    setMessages(prev => [...prev, userMsg]);
    const userInput = input;
    setInput('');
    setIsTyping(true);
    setTimeout(() => {
      const botMsg: ChatMessage = {
        id: Date.now() + 1,
        type: 'bot',
        text: getBotResponse(userInput),
        timestamp: new Date().toLocaleTimeString([], {
          hour: '2-digit',
          minute: '2-digit'
        })
      };
      setMessages(prev => [...prev, botMsg]);
      setIsTyping(false);
    }, 1200);
  };
  const handleQuickAction = (action: string) => {
    setInput(action);
  };
  return <div className="fixed bottom-4 right-4 sm:bottom-6 sm:right-6 z-50" data-id="element-68">
      {isOpen ? <div className="flex h-[calc(100vh-6rem)] sm:h-[540px] w-[calc(100vw-2rem)] sm:w-[380px] flex-col rounded-2xl bg-white shadow-2xl ring-1 ring-slate-900/5" data-id="element-69">
          {/* Header */}
          <div className="flex items-center justify-between rounded-t-2xl bg-navy-900 px-4 py-3 text-white" data-id="element-70">
            <div className="flex items-center gap-3" data-id="element-71">
              <div className="flex h-9 w-9 items-center justify-center rounded-full bg-gold-500" data-id="element-72">
                <BotIcon className="h-5 w-5 text-navy-900" data-id="element-73" />
              </div>
              <div data-id="element-74">
                <h3 className="font-semibold text-sm" data-id="element-75">Travel Assistant</h3>
                <div className="flex items-center gap-1.5" data-id="element-76">
                  <div className="h-1.5 w-1.5 rounded-full bg-emerald-400" data-id="element-77" />
                  <p className="text-[10px] text-slate-300" data-id="element-78">Online</p>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-1" data-id="element-79">
              <button onClick={() => setIsOpen(false)} className="rounded-full p-1.5 hover:bg-white/10 transition-colors" data-id="element-80">
                <XIcon className="h-4 w-4" data-id="element-81" />
              </button>
            </div>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-3 sm:p-4 space-y-3 bg-slate-50 custom-scrollbar" data-id="element-82">
            {messages.map(msg => <div key={msg.id} className={`flex ${msg.type === 'user' ? 'justify-end' : 'justify-start'}`} data-id="element-83">
                <div className={`flex items-end gap-2 max-w-[90%] sm:max-w-[85%] ${msg.type === 'user' ? 'flex-row-reverse' : ''}`} data-id="element-84">
                  <div className={`h-6 w-6 rounded-full flex items-center justify-center flex-shrink-0 ${msg.type === 'user' ? 'bg-navy-100' : 'bg-gold-100'}`} data-id="element-85">
                    {msg.type === 'user' ? <UserIcon className="h-3 w-3 text-navy-600" data-id="element-86" /> : <BotIcon className="h-3 w-3 text-gold-700" data-id="element-87" />}
                  </div>
                  <div data-id="element-88">
                    <div className={`rounded-2xl px-3 sm:px-4 py-2.5 text-sm leading-relaxed ${msg.type === 'user' ? 'bg-navy-900 text-white rounded-br-sm' : 'bg-white text-slate-700 shadow-sm border border-slate-100 rounded-bl-sm'}`} data-id="element-89">
                      {msg.text.split('\n').map((line, i) => <Fragment key={i} data-id="element-90">
                          {line}
                          {i < msg.text.split('\n').length - 1 && <br data-id="element-91" />}
                        </Fragment>)}
                    </div>
                    <span className={`text-[10px] text-slate-400 mt-1 block ${msg.type === 'user' ? 'text-right' : ''}`} data-id="element-92">
                      {msg.timestamp}
                    </span>
                  </div>
                </div>
              </div>)}

            {isTyping && <div className="flex justify-start" data-id="element-93">
                <div className="flex items-end gap-2" data-id="element-94">
                  <div className="h-6 w-6 rounded-full bg-gold-100 flex items-center justify-center flex-shrink-0" data-id="element-95">
                    <BotIcon className="h-3 w-3 text-gold-700" data-id="element-96" />
                  </div>
                  <div className="bg-white rounded-2xl rounded-bl-sm px-4 py-3 shadow-sm border border-slate-100" data-id="element-97">
                    <div className="flex gap-1" data-id="element-98">
                      <div className="h-2 w-2 rounded-full bg-slate-300 animate-bounce" style={{
                  animationDelay: '0ms'
                }} data-id="element-99" />
                      <div className="h-2 w-2 rounded-full bg-slate-300 animate-bounce" style={{
                  animationDelay: '150ms'
                }} data-id="element-100" />
                      <div className="h-2 w-2 rounded-full bg-slate-300 animate-bounce" style={{
                  animationDelay: '300ms'
                }} data-id="element-101" />
                    </div>
                  </div>
                </div>
              </div>}
            <div ref={messagesEndRef} data-id="element-102" />
          </div>

          {/* Disclaimer */}
          <div className="px-3 py-1.5 bg-amber-50 border-t border-amber-100" data-id="element-103">
            <p className="text-[9px] text-amber-700 flex items-center gap-1" data-id="element-104">
              <AlertTriangleIcon className="h-3 w-3 flex-shrink-0" data-id="element-105" />
              AI provides general guidance only. Not legal or immigration
              advice.
            </p>
          </div>

          {/* Input Area */}
          <div className="border-t border-slate-200 p-2.5 sm:p-3 bg-white rounded-b-2xl" data-id="element-106">
            <div className="flex gap-1.5 overflow-x-auto pb-2 mb-2 scrollbar-hide" data-id="element-107">
              {quickActions.map(action => <button key={action} className="whitespace-nowrap rounded-full border border-slate-200 bg-white px-2.5 py-1 text-[10px] font-medium text-slate-600 hover:bg-navy-50 hover:text-navy-700 hover:border-navy-200 transition-colors" onClick={() => handleQuickAction(action)} data-id="element-108">
                  {action}
                </button>)}
            </div>

            <div className="flex items-center gap-2" data-id="element-109">
              <button className="flex h-8 w-8 items-center justify-center rounded-full text-slate-400 hover:bg-slate-100 hover:text-navy-700 transition-colors flex-shrink-0" title="Escalate to Human" data-id="element-110">
                <PhoneIcon className="h-4 w-4" data-id="element-111" />
              </button>
              <form onSubmit={handleSend} className="flex flex-1 items-center gap-2" data-id="element-112">
                <input type="text" value={input} onChange={e => setInput(e.target.value)} placeholder="Type a message..." className="flex-1 min-w-0 rounded-full border border-slate-300 bg-slate-50 px-3 sm:px-4 py-2 text-sm focus:border-navy-600 focus:outline-none focus:ring-1 focus:ring-navy-600" data-id="element-113" />
                <button type="submit" disabled={!input.trim()} className="flex h-9 w-9 items-center justify-center rounded-full bg-navy-900 text-white transition-colors hover:bg-navy-700 disabled:opacity-40 flex-shrink-0" data-id="element-114">
                  <SendIcon className="h-4 w-4" data-id="element-115" />
                </button>
              </form>
            </div>
          </div>
        </div> : <button onClick={() => setIsOpen(true)} className="flex h-12 w-12 sm:h-14 sm:w-14 items-center justify-center rounded-full bg-navy-900 text-gold-400 shadow-lg shadow-navy-900/30 transition-transform hover:scale-105" data-id="element-116">
          <MessageSquareIcon className="h-6 w-6 sm:h-7 sm:w-7" data-id="element-117" />
        </button>}
    </div>;
}